import React, { useState, useEffect } from 'react';
import { Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';

/**
 * Calculate distance in feet between two lat/lon points using the Haversine formula.
 */
const distanceInFeet = (lat1, lon1, lat2, lon2) => {
  const R = 20902231; // Earth's radius in feet
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

/**
 * Check if a vehicle's alwaysShow override is currently active.
 */
const isAlwaysShowActiveForPopup = (vehicle) => {
  if (!vehicle.alwaysShow || !vehicle.alwaysShow.enabled) return false;

  const { startTime, endTime, timezone } = vehicle.alwaysShow;
  if (!startTime || !endTime) return false;

  try {
    const params = new URLSearchParams(window.location.search);
    const spoofTime = params.get('spoofTime');

    let nowInTz;
    if (spoofTime) {
      nowInTz = new Date(spoofTime);
    } else {
      const now = new Date();
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone || 'America/Los_Angeles',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
      const parts = formatter.formatToParts(now);
      const get = (type) => parts.find(p => p.type === type)?.value;
      nowInTz = new Date(`${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}:${get('second')}`);
    }

    return nowInTz >= new Date(startTime) && nowInTz <= new Date(endTime);
  } catch (e) {
    return false;
  }
};

/**
 * Get parade status for a vehicle with alwaysShow config active and no trip ID.
 */
const getParadeStatus = (vehicle) => {
  if (!vehicle.alwaysShow || !isAlwaysShowActiveForPopup(vehicle)) return null;
  if (vehicle.tripId) return null; // has a trip, use normal status

  const proximity = vehicle.alwaysShow.paradeProximity;
  if (proximity && vehicle.currentPosition) {
    const dist = distanceInFeet(
      vehicle.currentPosition[0],
      vehicle.currentPosition[1],
      proximity.lat,
      proximity.lon
    );
    if (dist <= proximity.radiusFeet) {
      return { status: vehicle.alwaysShow.notParading || 'Staging', color: '#FFA500' };
    } else {
      return { status: vehicle.alwaysShow.isParading || 'Parading!', color: '#28a745' };
    }
  }
  return { status: vehicle.alwaysShow.isParading || 'In Service', color: '#28a745' };
};

const VehicleLayer = ({ vehicles, campaignName, isUserLocation = false, pane, userLocationColor, vehicleArrowColor }) => {
  const [userLocationIcon, setUserLocationIcon] = useState(null);
  const [userLocationSvgText, setUserLocationSvgText] = useState(null);
  const [iconSizeCache, setIconSizeCache] = useState({});
  const map = useMap();

  const markerPaneProps = pane ? { pane } : {};

  // Check if user-location.svg exists, fallback to default-user-location.svg
  useEffect(() => {
    if (isUserLocation && campaignName) {
      const checkUserLocationIcon = async () => {
        const baseUrl = `${process.env.PUBLIC_URL || ''}/campaigns/${campaignName}/images`;
        const primaryIcon = `${baseUrl}/user-location.svg`;
        const fallbackIcon = `${baseUrl}/default-user-location.svg`;
        
        // Create a temporary image to test if the primary icon loads
        const img = new Image();
        
        img.onload = () => {
          // Primary icon loaded successfully
          setUserLocationIcon(primaryIcon);
          setUserLocationSvgText(null);
        };
        
        img.onerror = () => {
          // Primary icon failed to load, try fallback
          const fallbackImg = new Image();
          
          fallbackImg.onload = () => {
            // Fallback icon loaded successfully
            setUserLocationIcon(fallbackIcon);
            setUserLocationSvgText(null);
          };
          
          fallbackImg.onerror = () => {
            // Both icons failed, keep null (will use default Leaflet marker)
            console.warn(`Both user location icons failed to load for campaign: ${campaignName}`);
            setUserLocationIcon(null);
            setUserLocationSvgText(null);
          };
          
          fallbackImg.src = fallbackIcon;
        };
        
        img.src = primaryIcon;
      };
      
      checkUserLocationIcon();
    }
  }, [isUserLocation, campaignName]);

  useEffect(() => {
    if (!isUserLocation) return;
    if (!userLocationIcon) {
      setUserLocationSvgText(null);
      return;
    }

    const url = String(userLocationIcon);
    if (!url.toLowerCase().endsWith('.svg')) {
      setUserLocationSvgText(null);
      return;
    }

    fetch(url)
      .then((r) => (r.ok ? r.text() : null))
      .then((txt) => {
        if (typeof txt === 'string' && txt.includes('<svg')) {
          setUserLocationSvgText(txt);
        } else {
          setUserLocationSvgText(null);
        }
      })
      .catch(() => {
        setUserLocationSvgText(null);
      });
  }, [isUserLocation, userLocationIcon]);

  // Preload icons that should preserve their native aspect ratio and cache their scaled size
  useEffect(() => {
    if (!vehicles || !campaignName) return;

    const iconsNeedingPreserve = Array.from(
      new Set(
        vehicles
          .filter(v => v['icon-aspect-ratio'] === 'preserve' && v.icon)
          .map(v => v.icon)
      )
    ).filter(iconName => !iconSizeCache[iconName]);

    if (iconsNeedingPreserve.length === 0) return;

    iconsNeedingPreserve.forEach(iconName => {
      const img = new Image();
      const baseUrl = `${process.env.PUBLIC_URL || ''}/campaigns/${campaignName}/images/${iconName}`;

      img.onload = () => {
        const maxSize = 100; // larger box for preserve-aspect icons
        let { naturalWidth: w, naturalHeight: h } = img;

        if (!w || !h) {
          // Fallback to existing square size if dimensions aren't available
          w = maxSize;
          h = maxSize;
        } else {
          if (w > h) {
            const scale = maxSize / w;
            w = Math.round(maxSize);
            h = Math.round(h * scale);
          } else {
            const scale = maxSize / h;
            h = Math.round(maxSize);
            w = Math.round(w * scale);
          }
        }

        setIconSizeCache(prev => ({
          ...prev,
          [iconName]: [w, h]
        }));
      };

      img.onerror = () => {
        // If the icon fails to load, just let it fall back to the existing behavior
      };

      img.src = baseUrl;
    });
  }, [vehicles, campaignName, iconSizeCache]);

  // Create custom icons for vehicles
  const createVehicleIcon = (iconName, isUserLocation = false, aspectMode, heading, vehicleArrowColor) => {
    let iconUrl;

    if (isUserLocation) {
      // Use the determined user location icon, or fallback to default Leaflet marker
      iconUrl = userLocationIcon || 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png';
    } else {
      iconUrl = `${process.env.PUBLIC_URL || ''}/campaigns/${campaignName}/images/${iconName}`;
    }

    // Default behavior for all existing campaigns: fixed 56x56 square
    let iconSize = isUserLocation ? [32, 32] : [56, 56];

    // Only change size when explicitly requested via icon-aspect-ratio: "preserve"
    if (!isUserLocation && aspectMode === 'preserve' && iconName && iconSizeCache[iconName]) {
      iconSize = iconSizeCache[iconName];
    }

    const baseIconConfig = {
      iconUrl: iconUrl,
      iconRetinaUrl: iconUrl,
      iconSize,
      iconAnchor: isUserLocation ? [16, 16] : [16, 16],
      popupAnchor: [0, -32],
    };

    // For non-user-location vehicles with a heading, render composite icon with direction arrow
    if (!isUserLocation && heading != null && Number.isFinite(Number(heading))) {
      const baseXY = [40, 40];
      const arrowXY = [28, 28];
      const compositeXY = [54, 54];
      const safeHeading = ((Number(heading) % 360) + 360) % 360;
      const bucket = Math.round(safeHeading / 10) * 10;
      const color = typeof vehicleArrowColor === 'string' ? vehicleArrowColor : '#dc3545';

      const arrowSvgInline = `<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" fill="currentColor" class="bi bi-arrow-up-circle-fill" viewBox="0 0 16 16"><circle fill="white" cx="8" cy="8" r="7.2"/><path d="M16 8A8 8 0 1 0 0 8a8 8 0 0 0 16 0m-7.5 3.5a.5.5 0 0 1-1 0V5.707L5.354 7.854a.5.5 0 1 1-.708-.708l3-3a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1-.708.708L8.5 5.707z"/></svg>`;

      const html = `
        <div style="position: relative; width: ${compositeXY[0]}px; height: ${compositeXY[1]}px; overflow: visible;">
          <div style="position: absolute; left: 0; top: 0; width: ${baseXY[0]}px; height: ${baseXY[1]}px; display: flex; align-items: center; justify-content: center;">
            <img src="${iconUrl}" style="width: 100%; height: 100%;" />
          </div>
          <div style="position: absolute; right: 0; bottom: 0; width: ${arrowXY[0]}px; height: ${arrowXY[1]}px; display: flex; align-items: center; justify-content: center; color: ${color}; transform: rotate(${bucket}deg); transform-origin: 50% 50%; z-index: 999;">
            ${arrowSvgInline}
          </div>
        </div>
      `;

      return L.divIcon({
        html,
        className: '',
        iconSize: compositeXY,
        iconAnchor: [Math.round(compositeXY[0] / 2), Math.round(compositeXY[1] / 2)],
        popupAnchor: [0, -Math.round(compositeXY[1] / 2)]
      });
    }

    if (isUserLocation && typeof userLocationSvgText === 'string' && userLocationSvgText.includes('<svg')) {
      const color = (typeof userLocationColor === 'string' && userLocationColor.trim().length > 0)
        ? userLocationColor.trim()
        : null;

      const normalizedSvgBase = userLocationSvgText
        .replace(/\swidth="[^"]*"/i, '')
        .replace(/\sheight="[^"]*"/i, '');

      const normalizedSvgWithFill = normalizedSvgBase
        .replace(/<svg\b([^>]*)>/i, (match, attrs) => {
          if (/\sfill=\s*"[^"]*"/i.test(attrs)) return match;
          return `<svg${attrs} fill="currentColor">`;
        });

      const normalizedSvg = normalizedSvgWithFill
        .replace(/\scolor="[^"]*"/i, '')
        .replace(/<svg\s+([^>]*?)style="([^"]*)"/i, (match, before, styleValue) => {
          const cleaned = String(styleValue)
            .replace(/(^|;)\s*color\s*:[^;]*/gi, '')
            .trim();
          const nextStyle = `color: inherit;${cleaned ? ` ${cleaned}` : ''}`.trim();
          return `<svg ${before}style="${nextStyle}"`;
        })
        .replace(/<svg\s/i, '<svg style="color: inherit;" ');

      const html = `
        <div style="width: ${iconSize[0]}px; height: ${iconSize[1]}px; display: flex; align-items: center; justify-content: center;${color ? ` color: ${color};` : ''}">
          ${normalizedSvg}
        </div>
      `;

      return L.divIcon({
        html,
        className: '',
        iconSize,
        iconAnchor: baseIconConfig.iconAnchor,
        popupAnchor: baseIconConfig.popupAnchor
      });
    }

    return L.icon({
      ...baseIconConfig,
      ...(isUserLocation
        ? {}
        : {
          shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
          shadowSize: [41, 41],
          shadowAnchor: [12, 41]
        })
    });
  };

  // Get vehicle position - either current live position or fallback
  const getVehiclePosition = (vehicle) => {
    return vehicle.currentPosition || vehicle.fallbackCoordinates || [37.7749, -122.4194];
  };

  // Get vehicle status info
  const getVehicleStatus = (vehicle) => {
    if (isUserLocation) {
      return { status: 'Your Location', color: '#007bff' };
    }

    // Parade mode override
    const paradeStatus = getParadeStatus(vehicle);
    if (paradeStatus) return paradeStatus;
    
    if (vehicle.currentPosition) {
      if (vehicle.breakpointing) {
        return { status: 'Breakpointing', color: '#ffc107' };
      } else if (!vehicle.predictable) {
        return { status: 'Not Predictable', color: '#dc3545' };
      } else {
        return { status: 'In Service', color: '#28a745' };
      }
    } else {
      return { status: 'Out of Service', color: '#6c757d' };
    }
  };

  return (
    <>
      {vehicles.map((vehicle) => {
        const position = getVehiclePosition(vehicle);
        const icon = createVehicleIcon(
          vehicle.icon,
          isUserLocation,
          vehicle['icon-aspect-ratio'],
          vehicle.heading,
          vehicleArrowColor
        );
        const statusInfo = getVehicleStatus(vehicle);

        return (
          <Marker
            key={vehicle.id}
            position={position}
            icon={icon}
            vehicleId={vehicle.id}
            {...markerPaneProps}
          >
            <Popup pane="popupPane">
              <div style={{ minWidth: '200px' }}>
                <h4 style={{ margin: '0 0 8px 0', fontSize: '16px' }}>
                  {vehicle.name}
                </h4>
                
                {!isUserLocation && (
                  <>
                    <div style={{ marginBottom: '8px' }}>
                      <strong>Status:</strong>{' '}
                      <span style={{ color: statusInfo.color, fontWeight: 'bold' }}>
                        {statusInfo.status}
                      </span>
                    </div>
                    
                    {vehicle.route && (
                      <div style={{ marginBottom: '8px' }}>
                        <strong>Route:</strong> {vehicle.route.name}
                      </div>
                    )}
                    
                    {vehicle.tripData && (
                      <div style={{ marginBottom: '8px' }}>
                        <strong>Destination:</strong> {vehicle.tripData.trip_headsign}
                      </div>
                    )}

                    {/* Inside /public/campaigns/<campaignName>/iamges/ there might be an image that follows the vehicle name followed by an unscore then large. If there is an image, use it. Make the image fit within this existing element - dont make the parent element bigger.  */}

                    <div style={{ marginBottom: '8px' }}>
                      <img 
                        src={`${process.env.PUBLIC_URL || ''}/campaigns/${campaignName}/images/${vehicle.id}_large.png`} 
                        alt={vehicle.name}
                        style={{
                          maxWidth: '100%',
                          maxHeight: '120px',
                          width: 'auto',
                          height: 'auto',
                          objectFit: 'contain',
                          display: 'block'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>

                    {/* Zoom to vehicle button */}
                    <div style={{ marginBottom: '8px' }}>
                      <button
                        onClick={() => {
                          const vehiclePosition = getVehiclePosition(vehicle);
                          // Zoom level 16 shows approximately 3-4 blocks in each direction
                          map.setView(vehiclePosition, 16);
                        }}
                        style={{
                          backgroundColor: '#007bff',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '6px 12px',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          width: '100%',
                          transition: 'background-color 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#0056b3';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = '#007bff';
                        }}
                      >
                        Zoom to Vehicle
                      </button>
                    </div>

                  </>
                )}
                
                {isUserLocation && (
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    Your current location
                  </div>
                )}
              </div>
            </Popup>
          </Marker>
        );
      })}
    </>
  );
};

export default VehicleLayer;
